import React from "react";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";

interface ProductDeleteCounts {
  deletedCount: number;
  notDeletedCount: number;
}

interface ProductDeleteXlsModalProps {
  showPopup: boolean;
  /** True while the uploaded file is still being validated (before the delete job starts). */
  validating: boolean;
  /** The delete job's status; `"Completed"` switches the dialog to its result view. */
  status: string;
  /** S3 URL of the per-row report; only offered when some rows failed. */
  productDeleteLog: string;
  closeDelModal: () => void;
  setDownloadUrl: (url: string) => void;
  productDelete: ProductDeleteCounts;
  isManualDel: boolean;
}

/**
 * Progress + result of a bulk product delete via Upload XLS.
 *
 * On DS `Dialog` / `Alert` / `Spinner` / `Button` (was `ModalContainer` + `GMSButton` /
 * `GMSFillButton` + `Label` + Bootstrap `.progress`). Behaviour is unchanged: same props,
 * same three result messages, same buttons (Cancel; Download Log when rows failed, else
 * Done; a disabled Continue while the job runs).
 *
 * The in-progress bar is a DS `Spinner` now, not `Progress`: the old bar was hard-coded at
 * 25% because the job reports no percentage, and a determinate bar that never moves
 * claims progress nobody measured.
 *
 * Not dismissible by a click beside the panel (the legacy `clickOutSideClose={false}`).
 */
const ProductDeleteXlsModal = ({
  showPopup,
  validating,
  status,
  productDeleteLog,
  closeDelModal,
  setDownloadUrl,
  productDelete,
  isManualDel,
}: ProductDeleteXlsModalProps) => {
  const completed = status === "Completed";
  const { deletedCount, notDeletedCount } = productDelete;

  const downloadLog = () => {
    if (productDeleteLog) {
      setDownloadUrl(productDeleteLog);
      closeDelModal();
    }
  };

  const footer = (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
      <Button
        variant="secondary"
        data-testid="product-delete-xls-cancel-btn-2"
        onClick={() => closeDelModal()}
      >
        Cancel
      </Button>
      {completed && (notDeletedCount ? (
        <Button variant="primary" data-testid="product-delete-xls-download-log-btn" onClick={downloadLog}>
          Download Log
        </Button>
      ) : (
        <Button variant="primary" data-testid="product-delete-xls-done-btn" onClick={() => closeDelModal()}>
          Done
        </Button>
      ))}
      {!completed && (
        <Button variant="primary" data-testid="product-delete-xls-continue-btn" disabled>
          Continue
        </Button>
      )}
    </div>
  );

  return (
    <Dialog
      open={showPopup}
      onClose={() => closeDelModal()}
      title={isManualDel ? "Product Delete" : "Upload XLS"}
      footer={footer}
      data-testid="product-delete-xls-modal"
    >
      {completed ? (
        <div className="tw-flex tw-flex-col tw-gap-3" data-testid="product-delete-xls-result">
          {deletedCount > 0 && (
            <Alert tone="success" title="Successful" data-testid="product-delete-xls-success">
              {deletedCount} {deletedCount === 1 ? "Product" : "Products"} Successfully Deleted
            </Alert>
          )}
          {notDeletedCount > 0 && (
            <Alert tone="danger" title="Error" data-testid="product-delete-xls-error">
              {notDeletedCount} UID not match with the product
            </Alert>
          )}
          {deletedCount === 0 && notDeletedCount === 0 && (
            <Alert tone="danger" title="Error" data-testid="product-delete-xls-empty">
              Uploaded XLS file is empty
            </Alert>
          )}
        </div>
      ) : (
        <div className="tw-flex tw-justify-center tw-py-6" data-testid="product-delete-xls-progress">
          <Spinner size="md" showLabel label={validating ? "Validating XLS file" : "Deletion in progress"} />
        </div>
      )}
    </Dialog>
  );
};

export default ProductDeleteXlsModal;
